// BusinessCertificationModal.jsx
import React, { useMemo, useRef, useState, useLayoutEffect } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

/* ===== constants ===== */
const BASE_MD_WIDTH = 768;
const DPR = Math.min(
  3,
  Math.max(2, typeof window !== "undefined" ? window.devicePixelRatio || 1 : 2),
);

/* ===== utils ===== */

async function waitForImages(root) {
  const imgs = Array.from(root.querySelectorAll("img"));
  const holders = Array.from(root.querySelectorAll("[data-bg-src]"));

  const p1 = imgs.map((img) =>
    img.complete && img.naturalWidth > 0
      ? Promise.resolve()
      : new Promise((r) => {
          const done = () => {
            img.removeEventListener("load", done);
            img.removeEventListener("error", done);
            r();
          };
          img.addEventListener("load", done, { once: true });
          img.addEventListener("error", done, { once: true });
        }),
  );

  const p2 = holders.map((el) => {
    const url = el.getAttribute("data-bg-src");
    if (!url) return Promise.resolve();
    return new Promise((r) => {
      const i = new Image();
      i.crossOrigin = "anonymous";
      i.onload = i.onerror = () => r();
      i.src = url;
    });
  });

  await Promise.all([...p1, ...p2]);
}

function useScaledStage(baseWidth = BASE_MD_WIDTH) {
  const outerRef = useRef(null);
  const innerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [frameSize, setFrame] = useState({ w: baseWidth, h: 0 });

  const update = () => {
    const avail = outerRef.current ? outerRef.current.clientWidth : baseWidth;
    const s = Math.min(1, avail / baseWidth);
    setScale(s);
    if (innerRef.current) {
      setFrame({
        w: Math.round(baseWidth * s),
        h: Math.round((innerRef.current.offsetHeight || 0) * s),
      });
    }
  };

  useLayoutEffect(() => {
    update();
    const ro = new ResizeObserver(update);
    if (outerRef.current) ro.observe(outerRef.current);
    if (innerRef.current) ro.observe(innerRef.current);
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
  }, [baseWidth]);

  return { outerRef, innerRef, scale, frameSize };
}

function ScaledStage({
  children,
  baseWidth = BASE_MD_WIDTH,
  mobileTextBoost = 1.06,
}) {
  const { outerRef, innerRef, scale, frameSize } = useScaledStage(baseWidth);
  return (
    <div ref={outerRef} style={{ width: "100%" }}>
      <div
        style={{
          position: "relative",
          width: `${frameSize.w}px`,
          height: `${frameSize.h || 0}px`,
          margin: "0 auto",
          overflow: "hidden",
        }}
      >
        <div
          ref={innerRef}
          style={{
            width: baseWidth,
            transform: `scale(${scale})`,
            transformOrigin: "top left",
            "--text-boost": scale < 1 ? mobileTextBoost : 1,
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

function CertBlock({
  children,
  top,
  left = "50%",
  width = "80%",
  align = "center",
  size = 20,
  weight = 600,
  color = "#0e4976",
  letterSpacing = 0,
  lineHeight = 1.2,
  transform,
  fontFamily,
}) {
  return (
    <div
      style={{
        position: "absolute",
        top,
        left,
        transform: "translate(-50%, -50%)",
        width,
        textAlign: align,
        fontSize: `calc(var(--text-boost) * ${size}px)`,
        fontWeight: weight,
        color,
        letterSpacing,
        lineHeight,
        textTransform: transform,
        whiteSpace: "pre-line",
        fontFamily,
      }}
    >
      {children}
    </div>
  );
}

/* ===== single page ===== */
function BusinessCertificationPage({ data }) {
  const height = Math.round(BASE_MD_WIDTH * 1.414); // ~A4 portrait
  return (
    <div className="relative" style={{ width: BASE_MD_WIDTH, height }}>
      {/* background */}
      <div
        data-bg-src="/business_certification_background.jpg"
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: 'url("/business_certification_background.jpg")',
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />

      {/* overlay content */}
      <div style={{ position: "absolute", inset: 0 }}>
        {/* logo hiệp hội (center) */}
        <img
          src="/logo.png"
          alt="CCA Logo"
          style={{
            position: "absolute",
            top: "8%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            height: 90,
            objectFit: "contain",
          }}
        />

        {/* header tổ chức — hạ xuống tránh đè logo */}
        <CertBlock
          top="15.5%"
          width="72%"
          size={18}
          weight={500}
          color="#0e4976"
          lineHeight={1.3}
          fontFamily="'Times New Roman', Times, serif"
        >
          Hội Xây Dựng{"\n"}THÀNH PHỐ CẦN THƠ
        </CertBlock>

        {/* tiêu đề */}
        <CertBlock
          top="20.9%"
          width="84%"
          size={48}
          weight={700}
          color="#d23a2e"
          letterSpacing={2}
          transform="uppercase"
          fontFamily="'Times New Roman', Times, serif"
        >
          CHỨNG NHẬN
        </CertBlock>

        {/* logo thương hiệu (nếu có) */}
        {data.brandLogoUrl ? (
          <img
            src={data.brandLogoUrl}
            alt="Brand logo"
            style={{
              position: "absolute",
              top: "29.5%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              height: 67,
              objectFit: "contain",
            }}
          />
        ) : null}

        {/* tên công ty */}
        <CertBlock
          top="43%"
          width="90%"
          size={35}
          weight={700}
          color="#0e4976"
          lineHeight={1.25}
          transform="uppercase"
        >
          {data.companyName}
        </CertBlock>

        {/* địa chỉ */}
        <CertBlock
          top="55%"
          width="95%"
          size={30}
          weight={500}
          color="#0e4976"
          lineHeight={1.25}
        >
          {data.addressLine1}
          {"\n"}
          {data.addressLine2}
        </CertBlock>

        {/* ghi chú hội viên */}
        <CertBlock
          top="68%"
          width="78%"
          size={28}
          weight={500}
          color="#0e4976"
          lineHeight={1.35}
        >
          {data.memberNote}
        </CertBlock>

        {/* cột chữ ký bên phải */}
        <CertBlock
          top="80%"
          left="68%"
          width="52%"
          size={20}
          weight={500}
          color="#0e4976"
        >
          {data.approvedAt}
        </CertBlock>
        <CertBlock
          top="83%"
          left="65%"
          width="46%"
          size={21}
          weight={700}
          color="#0e4976"
        >
          {data.positionTitle}
        </CertBlock>
        <CertBlock
          top="93.5%"
          left="65%"
          width="46%"
          size={21}
          weight={700}
          color="#0e4976"
        >
          {data.signerName}
        </CertBlock>

        {/* số hiệu — xuống + sang phải thêm */}
        <CertBlock
          top="96%"
          left="38%"
          width="44%"
          size={21}
          weight={500}
          color="#0e4976"
          align="left"
          fontFamily="'Times New Roman', Times, serif"
        >
          {data.serialCode}
        </CertBlock>
      </div>
    </div>
  );
}

/* ===== modal (API giống UserPDFModal) ===== */
const BusinessCertificationModal = ({ isOpen, onClose, company }) => {
  const hiddenRef = useRef(null);
  const [busyPNG, setBusyPNG] = useState(false);
  const [busyPDF, setBusyPDF] = useState(false);

  // lấy dữ liệu từ company
  const companyName =
    company?.companyName || company?.name || company?.businessName || "";
  const address =
    company?.businessAddress ||
    company?.address ||
    company?.companyAddress ||
    "";

  const [addressLine1, addressLine2] = useMemo(() => {
    if (!address) return ["", ""];
    const parts = String(address).split(/,\s*/);
    if (parts.length <= 1) return [address, ""];
    const mid = Math.ceil(parts.length / 2);
    return [parts.slice(0, mid).join(", "), parts.slice(mid).join(", ")];
  }, [address]);

  const brandLogoUrl =
    company?.userAvatar ||
    company?.logo ||
    company?.companyAvatar ||
    company?.avatar ||
    company?.logoUrl ||
    "";

  const signerName = "";
  const positionTitle = company?.chairmanTitle || "CHỦ TỊCH";
  const placeDate =
    company?.certPlaceDate || "TP Cần Thơ, ngày 31 tháng 03 năm 2025";

  const serialCode = `Số: ......./HXD`;

  const now = new Date();

  const data = {
    brandLogoUrl,
    companyName: String(companyName).toUpperCase(),
    addressLine1,
    addressLine2,
    memberNote:
      "Là hội viên chính thức của\nHội Xây Dựng Thành phố Cần Thơ",
    placeDate,
    positionTitle,
    signerName,
    serialCode,
    approvedAt: `TP Cần Thơ, ngày....... tháng....... năm ${now.getFullYear()}`,
  };

  // biến thể LIÊN KẾT
  const dataLinked = {
    ...data,
    memberNote:
      "Là hội viên liên kết của\nHội Xây Dựng Thành phố Cần Thơ",
  };

  // exporters
  const hiddenRefLinked = useRef(null);
  const download = (dataUrl, filename) => {
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const exportPNG = async () => {
    if (!hiddenRef.current) return;
    setBusyPNG(true);
    try {
      await waitForImages(hiddenRef.current);
      const canvas = await html2canvas(hiddenRef.current, {
        useCORS: true,
        backgroundColor: null,
        scale: DPR,
        imageTimeout: 20000,
        removeContainer: true,
      });
      const safe = (data.companyName || "doanh_nghiep")
        .trim()
        .replace(/\s+/g, "_")
        .toLowerCase();
      download(canvas.toDataURL("image/png"), `chung_nhan_${safe}.png`);
    } finally {
      setBusyPNG(false);
    }
  };

  const exportPDF = async () => {
    if (!hiddenRef.current) return;
    setBusyPDF(true);
    try {
      await waitForImages(hiddenRef.current);
      const canvas = await html2canvas(hiddenRef.current, {
        useCORS: true,
        backgroundColor: null,
        scale: DPR,
        imageTimeout: 20000,
        removeContainer: true,
      });
      const img = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const PAGE_W = 210;
      const PAGE_H = 297;
      const PX_TO_MM = 25.4 / 96;

      let mmW = (canvas.width * PX_TO_MM) / DPR;
      let mmH = (canvas.height * PX_TO_MM) / DPR;
      const sc = Math.min(PAGE_W / mmW, PAGE_H / mmH, 1);
      mmW *= sc;
      mmH *= sc;

      pdf.addImage(
        img,
        "PNG",
        (PAGE_W - mmW) / 2,
        (PAGE_H - mmH) / 2,
        mmW,
        mmH,
      );

      const safe = (data.companyName || "doanh_nghiep")
        .trim()
        .replace(/\s+/g, "_")
        .toLowerCase();
      pdf.save(`chung_nhan_${safe}.pdf`);
    } finally {
      setBusyPDF(false);
    }
  };

  const [busyPNGLinked, setBusyPNGLinked] = useState(false);
  const [busyPDFLinked, setBusyPDFLinked] = useState(false);

  const exportPNGLinked = async () => {
    if (!hiddenRefLinked.current) return;
    setBusyPNGLinked(true);
    try {
      await waitForImages(hiddenRefLinked.current);
      const canvas = await html2canvas(hiddenRefLinked.current, {
        useCORS: true,
        backgroundColor: null,
        scale: DPR,
        imageTimeout: 20000,
        removeContainer: true,
      });
      const safe = (data.companyName || "doanh_nghiep")
        .trim()
        .replace(/\s+/g, "_")
        .toLowerCase();
      download(
        canvas.toDataURL("image/png"),
        `chung_nhan_lien_ket_${safe}.png`,
      );
    } finally {
      setBusyPNGLinked(false);
    }
  };

  const exportPDFLinked = async () => {
    if (!hiddenRefLinked.current) return;
    setBusyPDFLinked(true);
    try {
      await waitForImages(hiddenRefLinked.current);
      const canvas = await html2canvas(hiddenRefLinked.current, {
        useCORS: true,
        backgroundColor: null,
        scale: DPR,
        imageTimeout: 20000,
        removeContainer: true,
      });
      const img = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const PAGE_W = 210,
        PAGE_H = 297,
        PX_TO_MM = 25.4 / 96;
      let mmW = (canvas.width * PX_TO_MM) / DPR;
      let mmH = (canvas.height * PX_TO_MM) / DPR;
      const sc = Math.min(PAGE_W / mmW, PAGE_H / mmH, 1);
      mmW *= sc;
      mmH *= sc;
      pdf.addImage(
        img,
        "PNG",
        (PAGE_W - mmW) / 2,
        (PAGE_H - mmH) / 2,
        mmW,
        mmH,
      );
      const safe = (data.companyName || "doanh_nghiep")
        .trim()
        .replace(/\s+/g, "_")
        .toLowerCase();
      pdf.save(`chung_nhan_lien_ket_${safe}.pdf`);
    } finally {
      setBusyPDFLinked(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white w-5/6 lg:w-3/4 max-h-[90vh] overflow-y-auto rounded-lg p-0 lg:px-6">
        <div className="container mx-auto px-4 py-4">
          {/* preview (scaled) */}
          <ScaledStage>
            <BusinessCertificationPage data={data} />
          </ScaledStage>

          {/* hidden full-res for export */}
          <div
            ref={hiddenRef}
            style={{
              position: "absolute",
              left: "-10000px",
              top: 0,
              width: `${BASE_MD_WIDTH}px`,
              "--text-boost": 1,
            }}
            aria-hidden
          >
            <BusinessCertificationPage data={data} />
          </div>

          {/* hidden full-res for export (LIÊN KẾT) */}
          <div
            ref={hiddenRefLinked}
            style={{
              position: "absolute",
              left: "-10000px",
              top: 0,
              width: `${BASE_MD_WIDTH}px`,
              "--text-boost": 1,
            }}
            aria-hidden
          >
            <BusinessCertificationPage data={dataLinked} />
          </div>

          {/* actions */}
          <div className="sticky bottom-0 z-10 bg-white border-t mt-8">
            <div className="py-3 flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-outline btn-sm"
              >
                Đóng
              </button>
              <button
                type="button"
                onClick={exportPNG}
                disabled={busyPNG}
                className="btn btn-info btn-sm"
              >
                {busyPNG ? "Đang xử lý..." : "Xuất PNG"}
              </button>
              <button
                type="button"
                onClick={exportPDF}
                disabled={busyPDF}
                className="btn btn-success btn-sm"
              >
                {busyPDF ? "Đang xử lý..." : "Xuất PDF"}
              </button>

              <button
                type="button"
                onClick={exportPNGLinked}
                disabled={busyPNGLinked}
                className="btn btn-outline btn-info btn-sm"
                title="Xuất PNG hội viên liên kết"
              >
                {busyPNGLinked ? "Đang xử lý..." : "Xuất PNG (HVLK)"}
              </button>
              <button
                type="button"
                onClick={exportPDFLinked}
                disabled={busyPDFLinked}
                className="btn btn-outline btn-success btn-sm"
                title="Xuất PDF hội viên liên kết"
              >
                {busyPDFLinked ? "Đang xử lý..." : "Xuất PDF (HVLK)"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessCertificationModal;
